// PinLogin.tsx - SICHERES PIN-LOGIN MIT WARTEZEIT
import React, { useState, useEffect, useCallback } from 'react';

interface PinLoginProps {
  onSuccess: (user: string) => void;
  isChef?: boolean;
  authenticate: (name: string, pin: string) => Promise<boolean>;
}

interface AttemptTracker {
  count: number;
  lockedUntil: number | null;
  lastAttempt: number;
}

export default function PinLogin({ onSuccess, isChef = false, authenticate }: PinLoginProps) {
  const [pin, setPin] = useState('');
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [remainingTime, setRemainingTime] = useState(0);

  // Sicherheits-Konfiguration
  const MAX_ATTEMPTS = 3;
  const LOCKOUT_DURATIONS = [30, 60, 300, 900]; // 30s, 1min, 5min, 15min
  const STORAGE_KEY = `pin_attempts_${isChef ? 'chef' : 'employee'}`;

  // Lade Versuche aus localStorage
  const getAttemptTracker = useCallback((): AttemptTracker => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (e) {
      console.error('Error loading attempt tracker:', e);
    }
    return { count: 0, lockedUntil: null, lastAttempt: Date.now() };
  }, [STORAGE_KEY]);

  // Speichere Versuche
  const saveAttemptTracker = useCallback((tracker: AttemptTracker) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(tracker));
  }, [STORAGE_KEY]);

  // Prüfe ob gesperrt
  const checkLockStatus = useCallback(() => {
    const tracker = getAttemptTracker();
    if (tracker.lockedUntil && tracker.lockedUntil > Date.now()) {
      const remaining = Math.ceil((tracker.lockedUntil - Date.now()) / 1000);
      setRemainingTime(remaining);
      setError(`Zu viele Fehlversuche. Bitte warten Sie ${remaining} Sekunden.`);
      return true;
    }
    return false;
  }, [getAttemptTracker]);

  // Timer für Countdown
  useEffect(() => {
    const interval = setInterval(() => {
      const tracker = getAttemptTracker();
      if (tracker.lockedUntil && tracker.lockedUntil > Date.now()) {
        const remaining = Math.ceil((tracker.lockedUntil - Date.now()) / 1000);
        setRemainingTime(remaining);
        setError(`Zu viele Fehlversuche. Bitte warten Sie ${remaining} Sekunden.`);
      } else {
        setRemainingTime(0);
        if (tracker.lockedUntil) {
          // Reset nach Ablauf der Sperrzeit
          saveAttemptTracker({ count: 0, lockedUntil: null, lastAttempt: Date.now() });
          setError('');
        }
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [getAttemptTracker, saveAttemptTracker]);

  // Initial Check
  useEffect(() => {
    checkLockStatus();
  }, [checkLockStatus]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Prüfe Sperrung
    if (checkLockStatus()) {
      return;
    }

    setError('');
    setLoading(true);

    try {
      const user = isChef ? 'Chef' : selectedEmployee;
      
      // PIN-Validierung
      if (pin.length !== 4) {
        setError('PIN muss 4-stellig sein');
        setLoading(false);
        return;
      }

      const isValid = await authenticate(user, pin);
      
      if (isValid) {
        // Erfolgreiche Anmeldung - Reset Versuche
        saveAttemptTracker({ count: 0, lockedUntil: null, lastAttempt: Date.now() });
        
        // Session erstellen mit Zeitstempel
        sessionStorage.setItem('authenticated', 'true');
        sessionStorage.setItem('user', user);
        sessionStorage.setItem('authTime', Date.now().toString());
        
        onSuccess(user);
      } else {
        // Fehlgeschlagener Versuch
        const tracker = getAttemptTracker();
        const newCount = tracker.count + 1;
        
        if (newCount >= MAX_ATTEMPTS) {
          // Berechne Sperrzeit (exponentiell steigend)
          const lockoutIndex = Math.min(
            Math.floor(newCount / MAX_ATTEMPTS) - 1, 
            LOCKOUT_DURATIONS.length - 1
          );
          const lockoutDuration = LOCKOUT_DURATIONS[lockoutIndex] * 1000;
          const lockedUntil = Date.now() + lockoutDuration;
          
          saveAttemptTracker({
            count: newCount,
            lockedUntil: lockedUntil,
            lastAttempt: Date.now()
          });
          
          setRemainingTime(Math.ceil(lockoutDuration / 1000));
          setError(`Zu viele Fehlversuche! Gesperrt für ${lockoutDuration / 1000} Sekunden.`);
        } else {
          saveAttemptTracker({
            count: newCount,
            lockedUntil: null,
            lastAttempt: Date.now()
          });
          
          setError(`Ungültige PIN. ${MAX_ATTEMPTS - newCount} Versuche verbleibend.`);
        }
        
        setPin('');
      }
    } catch (err) {
      console.error('Auth error:', err);
      setError('Verbindungsfehler. Bitte versuchen Sie es später erneut.');
    } finally {
      setLoading(false);
    }
  };

  // Auto-Clear PIN nach Inaktivität
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (pin.length > 0 && !loading) {
        setPin('');
        setError('PIN aus Sicherheitsgründen zurückgesetzt.');
      }
    }, 30000); // 30 Sekunden

    return () => clearTimeout(timeout);
  }, [pin, loading]);

  const isLocked = remainingTime > 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl p-8 w-full max-w-md">
        <div className="text-center mb-8">
          <div className="w-20 h-20 bg-indigo-500 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
            <span className="text-white text-3xl">🔐</span>
          </div>
          <h1 className="text-2xl font-bold text-gray-800">
            {isChef ? 'Chef Dashboard' : 'Mitarbeiter App'}
          </h1>
          <p className="text-gray-600 mt-2">
            {isLocked ? '⏳ Zugang gesperrt' : 'PIN eingeben für Zugang'}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {!isChef && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Mitarbeiter auswählen
              </label>
              <select
                value={selectedEmployee}
                onChange={(e) => setSelectedEmployee(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                required
                disabled={isLocked}
              >
                <option value="">Bitte wählen...</option>
                <option value="Darek">Darek</option>
                <option value="Erhan">Erhan</option>
              </select>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              4-stellige PIN
            </label>
            <input
              type="password"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={4}
              value={pin}
              onChange={(e) => {
                const value = e.target.value;
                if (/^\d*$/.test(value)) {
                  setPin(value);
                  setError(''); // Clear error on new input
                }
              }}
              className={`w-full px-4 py-3 text-center text-2xl tracking-widest border-2 rounded-lg focus:outline-none ${
                isLocked 
                  ? 'border-red-300 bg-red-50' 
                  : 'border-gray-300 focus:border-indigo-500'
              }`}
              placeholder="••••"
              required
              disabled={loading || isLocked || (!isChef && !selectedEmployee)}
              autoComplete="off"
            />
            
            <div className="flex justify-center gap-2 mt-4">
              {[0, 1, 2, 3].map((index) => (
                <div
                  key={index}
                  className={`w-3 h-3 rounded-full transition-all ${
                    pin.length > index 
                      ? isLocked ? 'bg-red-500' : 'bg-indigo-500' 
                      : 'bg-gray-300'
                  }`}
                />
              ))}
            </div>
          </div>

          {error && (
            <div className={`px-4 py-3 rounded-lg text-sm ${
              isLocked 
                ? 'bg-red-100 border border-red-300 text-red-800' 
                : 'bg-red-50 border border-red-200 text-red-700'
            }`}>
              {error}
              {isLocked && remainingTime > 0 && (
                <div className="mt-2">
                  <div className="bg-red-200 rounded-full h-2 overflow-hidden">
                    <div 
                      className="bg-red-500 h-full transition-all duration-1000"
                      style={{ 
                        width: `${(remainingTime / (LOCKOUT_DURATIONS[0])) * 100}%` 
                      }}
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          <button
            type="submit"
            disabled={pin.length !== 4 || loading || isLocked || (!isChef && !selectedEmployee)}
            className={`w-full py-3 font-semibold rounded-lg transition-all ${
              pin.length === 4 && !loading && !isLocked && (isChef || selectedEmployee)
                ? 'bg-indigo-600 text-white hover:bg-indigo-700 shadow-md'
                : isLocked
                  ? 'bg-red-300 text-white cursor-not-allowed'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></span>
                Prüfe PIN...
              </span>
            ) : isLocked ? (
              `Gesperrt (${remainingTime}s)`
            ) : (
              'Anmelden'
            )}
          </button>
        </form>

        <div className="mt-8 text-center">
          <p className="text-xs text-gray-500">
            🔒 Sichere Verbindung • Werkstatt Management System
          </p>
          <p className="text-xs text-gray-400 mt-1">
            Nach 3 Fehlversuchen wird der Zugang temporär gesperrt
          </p>
        </div>
      </div>
    </div>
  );
}